/**
 * 列表页滚动控制条 + 筛选抽屉 + 表格区 ResizeObserver 共用逻辑。
 * 依赖页面 refs：scrollArea、filterAnchor、pageCardMain、drawerScroll、tableZone、multipleTable。
 */
export default {
  data() {
    return {
      filterScrollAwayThreshold: 0,
      showScrollControls: false,
      filterDrawerOpen: false,
      drawerPanelStyle: null,
      drawerPanelScrollable: false,
      scrollControlRafId: 0,
      suppressScrollControlsUntilTop: false,
      filterAnchorResizeObserver: null,
      tableLayoutObserver: null,
      tableLayoutRafId: 0,
      tableMeasureTimer: 0,
      tableContainerWidth: 0,
    }
  },
  computed: {
    hasRowSelection() {
      return (this.multipleSelection || []).length > 0
    },
    controlBarScrolled() {
      return this.showScrollControls && !this.filterDrawerOpen
    },
    controlBarDisplayLayout() {
      if (!this.hasRowSelection) {
        return 'info'
      }
      if (this.hasActiveScopeFilters) {
        return 'split'
      }
      return 'actions-full'
    },
    controlBarShowScrolledInfo() {
      return !this.hasRowSelection || this.controlBarDisplayLayout === 'split'
    },
    controlBarShowScrolledActions() {
      return this.hasRowSelection
    },
  },
  mounted() {
    const scrollEl = this.$refs.scrollArea
    if (scrollEl) {
      scrollEl.addEventListener('scroll', this.handleScrollAreaScroll, { passive: true })
    }
    window.addEventListener('resize', this.handleLayoutResize)
    this.$nextTick(() => {
      this.remeasureFilterBlockHeight()
      this.updateScrollControls()
      this.setupFilterAnchorResizeObserver()
      this.measureTableZoneWidth()
      this.setupTableLayoutObserver()
      this.scheduleTableLayout()
    })
  },
  beforeDestroy() {
    const scrollEl = this.$refs.scrollArea
    if (scrollEl) {
      scrollEl.removeEventListener('scroll', this.handleScrollAreaScroll)
    }
    window.removeEventListener('resize', this.handleLayoutResize)
    document.removeEventListener('keydown', this.handleFilterDrawerKeydown)
    if (this.filterAnchorResizeObserver) {
      this.filterAnchorResizeObserver.disconnect()
      this.filterAnchorResizeObserver = null
    }
    if (this.tableLayoutObserver) {
      this.tableLayoutObserver.disconnect()
      this.tableLayoutObserver = null
    }
    if (this.tableLayoutRafId) {
      cancelAnimationFrame(this.tableLayoutRafId)
      this.tableLayoutRafId = 0
    }
    if (this.tableMeasureTimer) {
      clearTimeout(this.tableMeasureTimer)
      this.tableMeasureTimer = 0
    }
    if (this.scrollControlRafId) {
      cancelAnimationFrame(this.scrollControlRafId)
      this.scrollControlRafId = 0
    }
  },
  methods: {
    bindLinkListScrollControl() {
      this.handleScrollAreaScroll = this.handleScrollAreaScroll.bind(this)
      this.handleLayoutResize = this.handleLayoutResize.bind(this)
      this.handleFilterDrawerKeydown = this.handleFilterDrawerKeydown.bind(this)
    },
    onLinkListDataRendered() {
      this.$nextTick(() => {
        this.remeasureFilterBlockHeight()
        this.updateScrollControls()
        this.measureTableZoneWidth()
        this.scheduleTableLayout()
        if (typeof this.syncTableSelectionFromStore === 'function') {
          this.syncTableSelectionFromStore()
        } else if (typeof this.checkedElementCheck === 'function') {
          this.checkedElementCheck()
        }
      })
    },
    remeasureFilterBlockHeight() {
      const anchor = this.$refs.filterAnchor
      this.filterScrollAwayThreshold = anchor ? anchor.offsetHeight : 0
    },
    setupFilterAnchorResizeObserver() {
      const anchor = this.$refs.filterAnchor
      if (!anchor || typeof ResizeObserver === 'undefined') {
        return
      }
      if (this.filterAnchorResizeObserver) {
        this.filterAnchorResizeObserver.disconnect()
      }
      this.filterAnchorResizeObserver = new ResizeObserver(() => {
        this.remeasureFilterBlockHeight()
        this.updateScrollControls()
      })
      this.filterAnchorResizeObserver.observe(anchor)
    },
    measureTableZoneWidth() {
      const zone = this.$refs.tableZone
      if (zone) {
        const nextWidth = Math.floor(zone.getBoundingClientRect().width)
        if (nextWidth > 0 && nextWidth !== this.tableContainerWidth) {
          this.tableContainerWidth = nextWidth
        }
        return
      }
      const tableEl = this.$refs.multipleTable && this.$refs.multipleTable.$el
      if (!tableEl) {
        return
      }
      const nextWidth = Math.floor(tableEl.clientWidth)
      if (nextWidth > 0 && nextWidth !== this.tableContainerWidth) {
        this.tableContainerWidth = nextWidth
      }
    },
    setupTableLayoutObserver() {
      const zone = this.$refs.tableZone
      if (!zone || typeof ResizeObserver === 'undefined') {
        return
      }
      if (this.tableLayoutObserver) {
        this.tableLayoutObserver.disconnect()
      }
      this.tableLayoutObserver = new ResizeObserver(() => {
        this.scheduleSettledTableMeasure()
      })
      this.tableLayoutObserver.observe(zone)
    },
    /**
     * 侧边栏宽度有 0.3 秒过渡，表格容器会连着变很多帧。
     * 每帧都重算列宽会把主线程打满，所以等连续变化停下来再量一次。
     */
    scheduleSettledTableMeasure() {
      if (this.tableMeasureTimer) {
        clearTimeout(this.tableMeasureTimer)
      }
      this.tableMeasureTimer = setTimeout(() => {
        this.tableMeasureTimer = 0
        this.measureTableZoneWidth()
        this.scheduleTableLayout()
      }, 50)
    },
    scheduleTableLayout() {
      if (this.tableLayoutRafId) {
        cancelAnimationFrame(this.tableLayoutRafId)
      }
      this.tableLayoutRafId = requestAnimationFrame(() => {
        this.tableLayoutRafId = 0
        const table = this.$refs.multipleTable
        if (table && typeof table.doLayout === 'function') {
          table.doLayout()
        }
      })
    },
    updateScrollControls() {
      if (this.filterDrawerOpen) {
        return
      }
      const scrollEl = this.$refs.scrollArea
      if (!scrollEl) {
        return
      }
      if (this.suppressScrollControlsUntilTop) {
        if (scrollEl.scrollTop <= 2) {
          this.suppressScrollControlsUntilTop = false
        } else {
          this.showScrollControls = false
          return
        }
      }
      const threshold = Math.max(0, this.filterScrollAwayThreshold - 1)
      this.showScrollControls = scrollEl.scrollTop >= threshold
    },
    handleScrollAreaScroll() {
      if (this.scrollControlRafId) {
        cancelAnimationFrame(this.scrollControlRafId)
      }
      this.scrollControlRafId = requestAnimationFrame(() => {
        this.scrollControlRafId = 0
        this.updateScrollControls()
      })
    },
    handleLayoutResize() {
      this.remeasureFilterBlockHeight()
      this.updateScrollControls()
      this.scheduleSettledTableMeasure()
      if (this.filterDrawerOpen) {
        this.syncDrawerLayout()
      }
    },
    syncDrawerLayout() {
      const main = this.$refs.pageCardMain
      if (!main) {
        return
      }
      const maxHeightPx = main.clientHeight
      this.drawerPanelStyle = {
        maxHeight: `${maxHeightPx}px`,
        height: 'auto',
      }
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const scroll = this.$refs.drawerScroll
          const panel = scroll && scroll.closest('.filterDrawerPanel')
          const header = panel && panel.querySelector('.filterDrawerPanel__header')
          const contentEl = scroll && scroll.querySelector('.linkListFilterPanel')
          if (!scroll || !header || !contentEl) {
            this.drawerPanelStyle = { maxHeight: `${maxHeightPx}px` }
            return
          }
          const scrollStyles = window.getComputedStyle(scroll)
          const scrollPaddingY =
            parseFloat(scrollStyles.paddingTop) + parseFloat(scrollStyles.paddingBottom)
          const contentHeight = contentEl.offsetHeight + scrollPaddingY
          const headerHeight = header.offsetHeight
          const naturalHeight = headerHeight + contentHeight
          const needsInternalScroll = naturalHeight > maxHeightPx
          const panelHeight = Math.min(maxHeightPx, naturalHeight)
          this.drawerPanelStyle = {
            height: `${panelHeight}px`,
            maxHeight: `${maxHeightPx}px`,
          }
          this.drawerPanelScrollable = needsInternalScroll
        })
      })
    },
    openFilterDrawer() {
      this.filterDrawerOpen = true
      document.addEventListener('keydown', this.handleFilterDrawerKeydown)
      this.$nextTick(() => {
        this.syncDrawerLayout()
        const drawerScroll = this.$refs.drawerScroll
        if (drawerScroll) {
          drawerScroll.scrollTop = 0
        }
      })
    },
    closeFilterDrawer() {
      if (!this.filterDrawerOpen) return
      this.filterDrawerOpen = false
      this.drawerPanelStyle = null
      this.drawerPanelScrollable = false
      document.removeEventListener('keydown', this.handleFilterDrawerKeydown)
      this.$nextTick(() => {
        this.updateScrollControls()
      })
    },
    handleFilterDrawerKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        this.closeFilterDrawer()
      }
    },
    scrollToTableTop() {
      const scrollEl = this.$refs.scrollArea
      if (!scrollEl) return
      this.closeFilterDrawer()
      this.suppressScrollControlsUntilTop = true
      this.showScrollControls = false
      scrollEl.scrollTo({ top: 0, behavior: 'smooth' })
    },
    resolveCategoryLabel(categoryId) {
      const match = (this.categoryOptions || []).find(
        (item) => String(item.id) === String(categoryId)
      )
      return (match && match.name) || String(categoryId)
    },
    resolveDataSetLabel(dataSetId) {
      const match = (this.dataOptions || []).find(
        (item) => String(item.id) === String(dataSetId)
      )
      return (match && match.name) || String(dataSetId)
    },
  },
}
